/**
 * COL-425: submit a move-in/move-out protocol on a tenant's behalf, for the
 * cases where the tenant cannot complete the mobile flow themselves.
 *
 * No signature is collected here — the note and/or attachments ARE the
 * evidence, filed under the admin's name (see submitProtocolOnBehalf in
 * TenantProfileDialog). Mirrors NotesThread's Composer for the "at least one
 * of note/attachment" gating.
 *
 * Submitting asks for a confirmation first: it files a protocol under the
 * admin's name with no tenant signature, and for a move-in it also marks the
 * tenant as moved in.
 */
import { useRef, useState } from "react";
import { FileText, Loader2, Upload, X } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";

const MAX_FILES = 20;
const MAX_BYTES = 20 * 1024 * 1024; // 20 MB

export function SubmitProtocolOnBehalfDialog({
  open,
  onOpenChange,
  kind,
  busy,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kind: "move_in" | "move_out" | null;
  busy: boolean;
  onSubmit: (args: { note: string; files: File[] }) => Promise<boolean>;
}) {
  const { t } = useLanguage();
  const [note, setNote] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const label =
    kind === "move_out"
      ? t("admin.protocol.moveOutLower", "move-out")
      : t("admin.protocol.moveInLower", "move-in");
  const canSubmit = (note.trim().length > 0 || files.length > 0) && !busy;

  function reset() {
    setNote("");
    setFiles([]);
  }

  function addFiles(next: File[]) {
    if (next.length === 0) return;
    const remaining = Math.max(0, MAX_FILES - files.length);
    const accepted = next.slice(0, remaining).filter((f) => {
      if (f.size > MAX_BYTES) return false;
      return true;
    });
    if (accepted.length > 0) setFiles((prev) => [...prev, ...accepted]);
  }

  async function handleSubmit() {
    if (!canSubmit) return;
    const ok = await onSubmit({ note: note.trim(), files });
    if (ok) {
      reset();
      onOpenChange(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent
        style={{ maxWidth: 560, maxHeight: "88vh", overflowY: "auto" }}
      >
        <DialogHeader>
          <DialogTitle style={{ fontSize: 17, fontWeight: 700 }}>
            {t(
              "admin.protocol.submitOnBehalf.title",
              "Submit the {label} protocol for the tenant"
            ).replace("{label}", label)}
          </DialogTitle>
          <DialogDescription style={{ fontSize: 13, color: "#64748B" }}>
            {t(
              "admin.protocol.submitOnBehalf.description",
              "Files under your name — no tenant signature is collected. Add a note, attach any scanned evidence, or both."
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t(
              "admin.protocol.submitOnBehalf.notePlaceholder",
              "What happened, and why you're filing this on their behalf…"
            )}
            rows={4}
            disabled={busy}
            className="w-full resize-none rounded-xl border border-neutral-200 bg-white p-3 text-sm outline-none placeholder:text-neutral-400 focus:border-emerald-500 disabled:opacity-60"
          />

          <div
            onDrop={(e) => {
              e.preventDefault();
              setIsDraggingOver(false);
              addFiles(Array.from(e.dataTransfer.files ?? []));
            }}
            onDragOver={(e) => {
              e.preventDefault();
              if (!isDraggingOver) setIsDraggingOver(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setIsDraggingOver(false);
            }}
            onClick={() => inputRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                inputRef.current?.click();
              }
            }}
            className={`flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed p-4 text-center transition-colors ${
              isDraggingOver
                ? "border-emerald-500 bg-emerald-50"
                : "border-neutral-200 hover:border-emerald-400 hover:bg-neutral-50"
            } ${busy ? "pointer-events-none opacity-60" : ""}`}
          >
            <Upload className="h-4 w-4 text-neutral-400" />
            <p className="text-sm font-medium text-neutral-700">
              {t(
                "admin.protocol.submitOnBehalf.dropzone",
                "Drag files here, or click to browse"
              )}
            </p>
            <p className="text-xs text-neutral-400">
              {t(
                "admin.protocol.submitOnBehalf.dropzoneHint",
                "Up to {max} files, 20 MB each"
              ).replace("{max}", String(MAX_FILES))}
            </p>
            <input
              ref={inputRef}
              type="file"
              multiple
              onChange={(e) => {
                addFiles(Array.from(e.target.files ?? []));
                e.target.value = "";
              }}
              className="hidden"
            />
          </div>

          {files.length > 0 && (
            <ul className="list-none space-y-1.5 p-0 m-0">
              {files.map((file, i) => (
                <li
                  key={`${file.name}-${i}`}
                  className="flex items-center gap-2 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm"
                >
                  <FileText className="h-4 w-4 shrink-0 text-neutral-400" />
                  <span className="flex-1 truncate">{file.name}</span>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 shrink-0"
                    disabled={busy}
                    onClick={() =>
                      setFiles((prev) => prev.filter((_, j) => j !== i))
                    }
                    aria-label={`${t("admin.protocol.submitOnBehalf.removePrefix", "Remove")} ${file.name}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </li>
              ))}
            </ul>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => {
                reset();
                onOpenChange(false);
              }}
            >
              {t("admin.notes.cancel", "Cancel")}
            </Button>
            <Button
              type="button"
              disabled={!canSubmit}
              onClick={() => setConfirming(true)}
            >
              {busy ? (
                <>
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  {t("admin.protocol.submitOnBehalf.submitting", "Submitting…")}
                </>
              ) : (
                t("admin.protocol.submitOnBehalf.submit", "Submit protocol")
              )}
            </Button>
          </div>
        </div>

        <AlertDialog open={confirming} onOpenChange={setConfirming}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t(
                  "admin.protocol.submitOnBehalf.confirmTitle",
                  "Submit this protocol on the tenant's behalf?"
                )}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {kind === "move_out"
                  ? t(
                      "admin.protocol.submitOnBehalf.confirmBodyMoveOut",
                      "It is filed under your name with no tenant signature and goes into the tenant's documents."
                    )
                  : t(
                      "admin.protocol.submitOnBehalf.confirmBodyMoveIn",
                      "It is filed under your name with no tenant signature, goes into the tenant's documents and marks the tenant as moved in."
                    )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={busy}>
                {t("admin.notes.cancel", "Cancel")}
              </AlertDialogCancel>
              <AlertDialogAction
                disabled={busy}
                onClick={() => void handleSubmit()}
              >
                {t("admin.protocol.submitOnBehalf.submit", "Submit protocol")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}
